import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { BetList } from "./components/bet-list/bet-list"
import { WinCelebration } from "./components/win-celebration/win-celebration"
import { LiveBadge } from "./components/live-badge/live-badge"
import { ReferralQrCode } from "./components/referral-qr-code/referral-qr-code"
import { useGlobal } from "./context/global-context"
import { useSocket } from "./context/socket-context"
import { decodeId, encodeId } from "./helpers"
import { BetSlipFragment } from "./graphql/core-types"
import { sortByNewest } from "./helpers/time-ago"
import { useOverlayBusy } from "./helpers/overlay-activity"
import { isBetCancelled, isBetNetPositive } from "./helpers/bet-line-helpers"
import { logger, LogContext, trackEvent } from "./helpers/logger"
import { betAttributes, pendingBetAttributes, settledBetAttributes } from "./helpers/bet-analytics"
import { getOverlaySettings, isStakersPreviewMode } from "./helpers/overlay-settings"
import { SAMPLE_PENDING_BET_SLIP, useSampleStakerCount, withSampleStakers } from "./helpers/sample-stakers"

const RESOLVED_HOLD_DURATION_MS = 4000

// bet_updated socket payload: the bets api slip camelCased, with raw uuids rather than graphql global ids.
// plc-app reads payout from it, the bets api's rest shape calls it payoutTotalAmount, so accept either
type BetUpdatedPayload = Omit<BetSlipFragment, 'id' | 'payout'> & {
  id: string
  payout?: string | number | null
  payoutTotalAmount?: string | number | null
}

export const TwitchOverlay = () => {
  const { getBetHistory, pendingBets, user } = useGlobal()
  const [wonBetSlip, setWonBetSlip] = useState<BetSlipFragment | null>(null)
  const lastStatusByBetIdRef = useRef<Map<string, string> | null>(null)
  const previousDisplayedBetIdRef = useRef<string | null>(null)
  // keyed by bet uuid, the socket and the refetch can both spot the same win
  const celebratedBetIdsRef = useRef(new Set<string>())
  // the loaded bets, read by the socket handler without resubscribing every refetch
  const allBetSlipsRef = useRef<BetSlipFragment[]>([])
  const { userChannel } = useSocket()

  // only celebrate when the streamer came out ahead on the whole bet, not refunds or payouts below the wager
  const celebrateWin = useCallback((betSlip: BetSlipFragment, source: 'socket' | 'refetch') => {
    const betUuid = decodeId(betSlip.id)
    if (celebratedBetIdsRef.current.has(betUuid) || !isBetNetPositive(betSlip)) return
    celebratedBetIdsRef.current.add(betUuid)
    // ?win=off skips the celebration, the settle itself is still reported as bet_settled
    if (getOverlaySettings().winAnimation === 'off') return
    trackEvent('win_celebration_shown', { ...settledBetAttributes(betSlip), style: getOverlaySettings().winAnimation, sound: getOverlaySettings().winSound, source })
    setWonBetSlip(betSlip)
  }, [])

  // the settle arrives on the socket with the full updated slip, the same way plc-app raises its win toast.
  // reacting to it directly doesn't depend on the settled bet showing up in the next 5 bet history refetch
  useEffect(() => {
    if (!userChannel) return
    const ref = userChannel.on('bet_updated', (payload: BetUpdatedPayload) => {
      if (!payload?.id || payload.status !== 'completed') return
      const payout = payload.payout ?? payload.payoutTotalAmount
      // the payload has no stake offer, take it from the loaded bet so the celebration can show what stakers won
      const loadedBetSlip = allBetSlipsRef.current.find((betSlip) => decodeId(betSlip.id) === payload.id)
      celebrateWin({
        ...payload,
        stakeOffer: payload.stakeOffer ?? loadedBetSlip?.stakeOffer ?? null,
        id: encodeId('BetSlip', payload.id),
        payout: payout === null || payout === undefined ? null : String(payout),
        betTotalAmount: String(payload.betTotalAmount),
      }, 'socket')
    })
    return () => userChannel.off('bet_updated', ref)
  }, [userChannel, celebrateWin])

  // blocking, so even an overdue scheduled refresh waits for the celebration to finish rather than cutting it off
  useOverlayBusy('win-animation', wonBetSlip !== null, { blocking: true })

  // cancelled bets were refunded in full, plc-app's history treats them as void so they leave the overlay entirely
  const pendingBetSlips = useMemo(
    () => sortByNewest((pendingBets?.betSlips || []).filter((betSlip): betSlip is BetSlipFragment => Boolean(betSlip) && !isBetCancelled(betSlip!))),
    [pendingBets],
  )

  // pending bets plus recent history, so the newest bet always has a card even if it's older than the history page
  // and a just-resolved bet can stay on screen after it drops out of the pending list.
  // cancelled bets stay in this one so their status change can still be reported
  const allBetSlips = useMemo(() => {
    const betSlipsById = new Map<string, BetSlipFragment>()
    pendingBetSlips.forEach((betSlip) => betSlipsById.set(betSlip.id, betSlip))
    getBetHistory?.betSlips?.forEach((betSlip) => {
      if (betSlip) betSlipsById.set(betSlip.id, betSlip)
    })
    return sortByNewest(Array.from(betSlipsById.values()))
  }, [pendingBetSlips, getBetHistory])
  allBetSlipsRef.current = allBetSlips

  // history can be fresher than the pending list, so drop cancellations after merging
  const betSlips = useMemo(() => allBetSlips.filter((betSlip) => !isBetCancelled(betSlip)), [allBetSlips])

  const newestPendingBetId = betSlips.find((betSlip) => betSlip.status === 'pending')?.id || null
  // the data is already loaded on mount, so start on the newest bet rather than the empty state.
  // starting on null swapped the card's contents and height while the panel was still sliding in
  const [displayedBetId, setDisplayedBetId] = useState<string | null>(newestPendingBetId)
  const displayedBetStatus = betSlips.find((betSlip) => betSlip.id === displayedBetId)?.status || null

  useEffect(() => {
    // keep showing the resolved bet for a beat so the outcome is visible before it slides out
    if (displayedBetStatus && displayedBetStatus !== 'pending') {
      // go straight to the next bet so the card swaps its contents rather than hiding in between
      const holdTimeout = setTimeout(() => setDisplayedBetId(newestPendingBetId), RESOLVED_HOLD_DURATION_MS)
      return () => clearTimeout(holdTimeout)
    }

    // a newly placed bet takes over the main card and the one it replaces drops into other pending
    if (displayedBetId !== newestPendingBetId) setDisplayedBetId(newestPendingBetId)
  }, [displayedBetId, displayedBetStatus, newestPendingBetId])

  useEffect(() => {
    const lastStatusById = lastStatusByBetIdRef.current
    lastStatusByBetIdRef.current = new Map(allBetSlips.map((betSlip) => [betSlip.id, betSlip.status]))

    // establish a baseline on first load so bets that already settled before we loaded don't trigger the animation
    if (lastStatusById === null) {
      trackEvent('overlay_ready', {
        pendingBets: pendingBetSlips.length,
        recentBets: allBetSlips.length,
        hasReferralCode: Boolean(user?.referralCodes?.some((referralCode) => referralCode?.active)),
      })
      return
    }

    allBetSlips.forEach((betSlip) => {
      const previousStatus = lastStatusById.get(betSlip.id)
      if (previousStatus === undefined && betSlip.status === 'pending') {
        trackEvent('bet_received', pendingBetAttributes(betSlip))
        return
      }
      if (previousStatus !== 'pending' || betSlip.status === 'pending') return
      if (isBetCancelled(betSlip)) {
        trackEvent('bet_cancelled', { ...betAttributes(betSlip), refundReason: betSlip.refundReason })
        logger.info(LogContext.BETS, 'Bet cancelled, removed from overlay', { betId: betAttributes(betSlip).betId })
        return
      }
      trackEvent('bet_settled', settledBetAttributes(betSlip))
    })

    // backstop for a missed socket message: a bet we saw pending that the refetch now shows settled
    const newlyWonBetSlip = allBetSlips.find((betSlip) =>
      lastStatusById.get(betSlip.id) === 'pending'
      && isBetNetPositive(betSlip)
    )
    if (newlyWonBetSlip) celebrateWin(newlyWonBetSlip, 'refetch')
    // only bet changes should report, user and the pending count are read for the ready event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBetSlips])

  const displayedBetSlip = betSlips.find((betSlip) => betSlip.id === displayedBetId) || null

  // which bet the main card shows, and why it changed, is the core usage signal for the overlay
  useEffect(() => {
    const previousBetId = previousDisplayedBetIdRef.current
    previousDisplayedBetIdRef.current = displayedBetId
    if (displayedBetId === previousBetId) return
    if (!displayedBetSlip) {
      logger.info(LogContext.BETS, 'No pending bets, showing empty state')
      return
    }
    trackEvent('bet_displayed', {
      ...pendingBetAttributes(displayedBetSlip),
      pendingCount: pendingBetSlips.length,
      replacedPending: previousBetId !== null && pendingBetSlips.some((betSlip) => betSlip.id === previousBetId),
    })
    // keyed on the displayed id, the slip and pending list are just read for the event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedBetId])
  const betSlipIds = new Set(betSlips.map((betSlip) => betSlip.id))
  const olderPendingBetSlips = pendingBetSlips.filter((betSlip) => betSlip.id !== displayedBetId && betSlipIds.has(betSlip.id))

  // the url builder's stakers preview backs the shown bet (or a sample one if there's none) with made up stakers
  const isStakersPreview = isStakersPreviewMode()
  const sampleStakerCount = useSampleStakerCount(isStakersPreview)
  const cardBetSlip = isStakersPreview
    ? withSampleStakers(displayedBetSlip ?? SAMPLE_PENDING_BET_SLIP, sampleStakerCount)
    : displayedBetSlip

  const referralCode = user?.referralCodes?.find((referralCode) => referralCode?.active)?.code
  // the win celebration isn't positional, it has its own ?win= setting and plays even with the bet card off
  const { positions, winAnimation, winSound } = getOverlaySettings()

  return (
    <>
    {positions.liveBadge !== 'off' && <LiveBadge side={positions.liveBadge} />}
    {positions.betList !== 'off' && (
      <BetList side={positions.betList} betSlip={cardBetSlip} olderPendingBetSlips={olderPendingBetSlips} />
    )}
    {positions.referralQr !== 'off' && <ReferralQrCode code={referralCode} side={positions.referralQr} />}
    {wonBetSlip && winAnimation !== 'off' && (
      <WinCelebration
        key={wonBetSlip.id}
        style={winAnimation}
        sound={winSound}
        betSlip={wonBetSlip}
        betList={positions.betList}
        onComplete={() => setWonBetSlip(null)}
      />
    )}
    </>
  )
}